import React, { useState } from 'react';
import { isEmpty } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import Text from '~/components/core/TextComponents/Text';
import { PencilIcon } from '~/components/icons';
import Collapse from '~/components/ui/atoms/Collapse';

import AgentConfigChip, { AGENT_CHIP_TONE } from '../AgentConfigChip';
import copies from '../copies';

interface GeneralInstructionsCardProps {
  generalInstructions: string;
  onEdit: () => void;
}

/**
 * The scope's general instructions, read-only here and edited in a dialog.
 *
 * Collapsed by default: the instructions are prose, often long, and expanded they pushed the
 * stages and actions off the screen on arrival. The header carries a "Not set" chip so the one
 * state an operator has to act on is still visible while the body is closed.
 */
const GeneralInstructionsCard: React.FC<GeneralInstructionsCardProps> = ({ generalInstructions, onEdit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasInstructions = !isEmpty(generalInstructions);

  return (
    <div className="rounded-md border border-solid border-slate-500">
      <div className="flex items-center gap-8 border-0 border-b border-solid border-slate-400 px-16 py-12">
        <button
          type="button"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          aria-expanded={isOpen}
          className="flex flex-1 cursor-pointer items-center gap-8 border-0 bg-transparent p-0 text-left"
        >
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            {copies.editor.generalInstructionsTitle}
          </Text>
          {hasInstructions ? null : (
            <AgentConfigChip label={copies.editor.generalInstructionsNotSet} tone={AGENT_CHIP_TONE.ATTENTION} />
          )}
        </button>
        <Button variant="text" color="primary" startIcon={<PencilIcon />} onClick={onEdit}>
          {copies.editor.edit}
        </Button>
      </div>
      {/* Hidden rather than unmounted, matching the sibling cards: the text is a prop, so nothing
          is re-read either way. */}
      <Collapse in={isOpen}>
        <div className="p-16">
          {/* pre-wrap because the instructions are authored as prose with deliberate line
              breaks, and the read-only preview has to show them as the agent will read them. */}
          <div className="whitespace-pre-wrap rounded-md border border-solid border-slate-500 bg-slate-100 p-16">
            <Text
              variant={Text.VARIANTS.SM}
              colorVariant={hasInstructions ? Text.COLOR_VARIANTS.PRIMARY : Text.COLOR_VARIANTS.SECONDARY}
            >
              {hasInstructions ? generalInstructions : copies.editor.generalInstructionsEmpty}
            </Text>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default GeneralInstructionsCard;
